import Stack from "@mui/material/Stack";
import { useTranslation } from "@privacy/translations";
import { Navigate, Route, Routes, useParams } from "react-router";
import { PageSpinner, PageTitle, PageWrapper } from "../../../shared/Page";
import { usePrivacyDocumentTitle } from "../../../shared/usePrivacyDocumentTitle";
import { prefetchTemplateFormSettingsById, useGetTemplateFormTypes } from "../../../api/templateFormSettings";
import type { TemplateFormType } from "../../../api/templateFormSettings.types";
import { TemplateFormCard } from "./TemplateFormCard";
import { TemplateFormDialog } from "./TemplateFormSectionsDialog";
import { TemplateProvider } from "./TemplateFormProvider";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { useCurrentCustomer } from "../../../root/CurrentOrganisationProvider";

const VisibleTemplateFormDialog = ({ visibleFormIds, onClose }: { visibleFormIds: string[]; onClose: () => void }) => {
  const { paths } = usePrivacyNavigate();
  const { templateFormId } = useParams<{ templateFormId: string }>();

  if (!templateFormId || !visibleFormIds.includes(templateFormId))
    return <Navigate to={paths.templateForms()} replace />;

  return <TemplateFormDialog onClose={onClose} />;
};

export const TemplatePage = () => {
  usePrivacyDocumentTitle("Sidebar.Templates");
  const { t } = useTranslation();
  const { paths, navigate } = usePrivacyNavigate();

  const { data } = useGetTemplateFormTypes();
  const currentCustomer = useCurrentCustomer();

  if (!data) return <PageSpinner />;

  const getFormByFormType = (formType: TemplateFormType["formType"]) =>
    data.forms.find(form => form.formType === formType);

  const isForm = (form: TemplateFormType | undefined): form is TemplateFormType => Boolean(form);

  const orderedForms = [
    getFormByFormType("REGISTRATIONS"),
    getFormByFormType("PRIVACY_PROCESSOR_REGISTRATIONS"),
    getFormByFormType("SYSTEMS"),
    currentCustomer.customerKind !== "enterprise" ? getFormByFormType("PERSONAL_DATA_CONTROLLERS") : undefined,
    getFormByFormType("DATA_PROTECTION_IMPACT_ASSESSMENT"),
  ].filter(isForm);

  return (
    <PageWrapper>
      <Stack sx={{ flexWrap: "wrap" }}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", gap: 2, pb: 5, alignItems: "flex-end", flexWrap: "wrap" }}
        >
          <PageTitle title={t("Template.Title")} subTitle={t("Template.SubTitle")} />
        </Stack>
        <Stack sx={{ gap: 4, width: "100%", maxWidth: 700 }}>
          {orderedForms.map(form => (
            <TemplateFormCard
              onHover={() => prefetchTemplateFormSettingsById(form.id)}
              key={form.id}
              form={form}
              onEditClick={() => navigate(form.id)}
            />
          ))}
        </Stack>
      </Stack>
      <Routes>
        <Route
          path=":templateFormId/*"
          element={
            <TemplateProvider>
              <VisibleTemplateFormDialog
                visibleFormIds={orderedForms.map(form => form.id)}
                onClose={() => navigate(paths.templateForms())}
              />
            </TemplateProvider>
          }
        />
      </Routes>
    </PageWrapper>
  );
};
